import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {FrontKeyboardEvent} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {CheckBox} from '../../../../../libs/shared/ui/src/components/booleanInputs/checkBox';
import {StyledBoxDiv} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {boxedInputColorReaders} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {DropdownItem} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useLocalize} from '../../../../../srcCommon/internationalization/internationalizationContext';
import {TokenBase} from '../../../../components/common/editor/tokens/tokenBase';
import {TokensEditor} from '../../../../components/common/editor/tokens/tokensEditor';
import type {FileExtensionParameterErrorsEnum} from '../parameters/fileExtensionParameterErrors';
import {fileExtensionParameterErrorMessages} from '../parameters/fileExtensionParameterErrors';

/*
 * Styles.
 */

const TokensEditorGrid = styled.div`
  grid-area: secondary-content;
  width: 100%;
  display: grid;
  grid-template-areas:
    'input checkbox'
    'error .';
  grid-template-columns: 1fr 16px;
  grid-column-gap: 12px;
`;

const StyledInputWrapper = styled(StyledBoxDiv)`
  grid-area: input;
  padding: 2px 5px;
`;

const StyledCheckboxWrapper = styled.div`
  grid-area: checkbox;
  ${flexCenter};
`;

const StyledErrorWrapper = styled.div`
  grid-area: error;
  font-weight: ${fontWeights.medium};
  font-size: ${fontSizes.verySmall};
  color: ${boxedInputColorReaders.textError};
  margin-top: 6px;
`;

/*
 * Types.
 */

interface TokensEditorInputProps {
  tokens: ReadonlyArray<string>;
  delimiters: RegExp;
  input: string;
  setInput: (input: string) => void;
  validateInput: (input: string) => FileExtensionParameterErrorsEnum | undefined;
  onTokensChange: (tokens: ReadonlyArray<string>) => void;
}

/*
 * Component.
 */

export const TokensEditorInput: FC<TokensEditorInputProps> = ({
  input,
  tokens,
  delimiters,
  validateInput,
  setInput,
  onTokensChange,
}) => {
  const [error, setError] = useState<FileExtensionParameterErrorsEnum>();
  const localize = useLocalize();

  const onTextChange = (text: string) => {
    const delimiterIndex = text.search(delimiters);
    const before = text.slice(0, delimiterIndex);
    const after = text.slice(delimiterIndex + 1);

    if (delimiterIndex === 0) {
      setError(undefined);
      // Do not display delimiters.
      setInput(after);
      return;
    }

    const inputError = validateInput(text);
    setError(inputError);

    if (delimiterIndex < 0) {
      // Do not display delimiters.
      setInput(text);
      return;
    }

    if (inputError) {
      setInput(before);
      return;
    }

    setInput(after);
    onTokensChange([...tokens, before]);
  };

  const tryInputValidation = () => {
    if (input) {
      onTextChange(`${input} `);
    }
  };

  const onKeyDown = (event: FrontKeyboardEvent) => {
    if (event.key === 'Tab') {
      event.preventDefault();
      tryInputValidation();
    }
  };

  const onKeyUp = (event: FrontKeyboardEvent) => {
    if (event.key === 'Enter') {
      tryInputValidation();
    }
  };

  const onRemoveTokensClick = (tokenIndexes: ReadonlyArray<number>) =>
    onTokensChange(tokens.filter((_, index) => !tokenIndexes.includes(index)));

  const onEditTokenClick = (tokenIndex: number) => {
    setInput(tokens[tokenIndex]);
    onTokensChange(tokens.filter((_, index) => index !== tokenIndex));
  };

  const renderToken = (index: number, isSelected: boolean) => (
    <TokenBase isSelected={isSelected}>{tokens[index]} </TokenBase>
  );

  return (
    <DropdownItem isNotInteractive>
      <TokensEditorGrid>
        <StyledInputWrapper
          onKeyUp={onKeyUp}
          onKeyDown={onKeyDown}
          $size={VisualSizesEnum.MEDIUM}
          $noMargin
          $hasError={Boolean(error)}
        >
          <TokensEditor
            text={input}
            tokenIds={tokens.map((token, index) => `tokens-editor-input-token-${index}`)}
            renderToken={renderToken}
            onRemoveTokensClick={onRemoveTokensClick}
            onEditTokenClick={onEditTokenClick}
            onTextChange={onTextChange}
            shouldFocus
          />
        </StyledInputWrapper>
        <StyledCheckboxWrapper>
          {tokens.length > 0 && <CheckBox isChecked isDisabled onChange={() => {}} />}
        </StyledCheckboxWrapper>
        {error && (
          <StyledErrorWrapper>{localize(fileExtensionParameterErrorMessages[error])}</StyledErrorWrapper>
        )}
      </TokensEditorGrid>
    </DropdownItem>
  );
};
